// CartDrawer.jsx
// Slide-in cart panel, triggered by the cart badge on /shop (and anywhere else
// we add it later). Reads cart state via useCart(), mutates via the cart
// utility's updateQuantity / removeFromCart.
//
// Slice 1.2: UI only. Checkout button is present but disabled with a "coming
// soon" note — Slice 1.3 wires it to createShopCheckout + Square.
//
// QtyStepper is exported separately because ShopPage's item modal needs the
// same control to let visitors pick N before adding.

import { useEffect } from "react";
import { useCart, updateQuantity, removeFromCart, cartSubtotalCents, cartCount, formatPrice } from "../utils/cart";

// ── Shared quantity stepper ──────────────────────────────────────────────
export function QtyStepper({ value, onChange, min = 1, max = 99, compact = false, disabled = false }) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  const size = compact ? 28 : 34;
  const font = compact ? "1rem" : "1.1rem";
  const wrap = {
    display: "inline-flex",
    alignItems: "center",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,218,173,0.25)",
    borderRadius: "999px",
    overflow: "hidden",
    opacity: disabled ? 0.5 : 1,
  };
  const btn = {
    width: `${size}px`,
    height: `${size}px`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    color: "var(--jop-cream)",
    border: "none",
    fontSize: font,
    fontWeight: 700,
    cursor: disabled ? "default" : "pointer",
    fontFamily: "inherit",
  };
  const display = {
    minWidth: compact ? "32px" : "40px",
    textAlign: "center",
    color: "white",
    fontWeight: 800,
    fontSize: compact ? "0.92rem" : "1rem",
    fontFamily: "inherit",
  };
  return (
    <div style={wrap} role="group" aria-label="Quantity">
      <button type="button" onClick={dec} disabled={disabled || value <= min} aria-label="Decrease" style={btn}>−</button>
      <span style={display}>{value}</span>
      <button type="button" onClick={inc} disabled={disabled || value >= max} aria-label="Increase" style={btn}>+</button>
    </div>
  );
}

// ── Cart badge button ────────────────────────────────────────────────────
// Small round pill, shows item count. Used in the /shop top bar to open
// the drawer. Hidden entirely when cart is empty so the page doesn't
// nag visitors who aren't shopping yet.
export function CartBadgeButton({ onClick, hideWhenEmpty = true }) {
  const items = useCart();
  const count = cartCount(items);
  if (hideWhenEmpty && count === 0) return null;
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.4rem",
        background: "var(--jop-gold)",
        color: "#1a0a12",
        border: "none",
        borderRadius: "999px",
        padding: "0.45rem 0.95rem",
        fontFamily: "var(--jop-display)",
        fontSize: "0.88rem",
        fontWeight: 800,
        cursor: "pointer",
        boxShadow: "0 4px 14px rgba(252,179,31,0.35)",
        letterSpacing: "0.01em",
      }}
    >
      🛒 Cart · {count}
    </button>
  );
}

// ── Cart drawer ──────────────────────────────────────────────────────────
export default function CartDrawer({ open, onClose }) {
  const items = useCart();
  const subtotal = cartSubtotalCents(items);
  const hasItems = items.length > 0;

  // Navigate to our custom /shop/checkout page. It handles the shipping form,
  // Shippo rate lookup, review, then hands off to Square for the card entry
  // (Web Payments SDK replaces that Square bounce in the next slice).
  function handleCheckout() {
    if (!hasItems) return;
    onClose();
    window.location.href = "/shop/checkout";
  }

  // Escape to close + body scroll lock while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <style>{`
        .jop-cart-backdrop {
          position: fixed; inset: 0;
          background: rgba(6,12,25,0.7);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          z-index: 250;
          animation: jopCartFade 0.2s ease;
        }
        .jop-cart-panel {
          position: fixed; top: 0; right: 0; bottom: 0;
          width: 100%;
          max-width: 440px;
          background: #0b1d34;
          border-left: 1px solid rgba(252,179,31,0.3);
          box-shadow: -20px 0 60px rgba(0,0,0,0.5);
          color: var(--jop-cream);
          z-index: 251;
          display: flex; flex-direction: column;
          animation: jopCartSlide 0.25s cubic-bezier(0.2,0.8,0.25,1);
        }
        .jop-cart-body {
          flex: 1;
          overflow-y: auto;
          padding: 0.75rem 1rem 1rem;
          scrollbar-width: thin;
          scrollbar-color: rgba(255,218,173,0.22) transparent;
        }
        .jop-cart-body::-webkit-scrollbar { width: 8px; }
        .jop-cart-body::-webkit-scrollbar-track { background: transparent; }
        .jop-cart-body::-webkit-scrollbar-thumb {
          background: rgba(255,218,173,0.22);
          border-radius: 8px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }
        @keyframes jopCartFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes jopCartSlide { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>

      <div className="jop-cart-backdrop" onClick={onClose} />
      <aside className="jop-cart-panel" role="dialog" aria-label="Cart" style={{ fontFamily: "var(--jop-display)" }}>

        {/* Header */}
        <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid rgba(255,218,173,0.15)", display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <h2 style={{ margin: 0, fontFamily: "var(--jop-script)", color: "var(--jop-gold)", fontWeight: 700, fontSize: "1.8rem", flex: 1 }}>Your cart</h2>
          <button
            onClick={onClose}
            aria-label="Close cart"
            style={{ background: "transparent", border: "1px solid rgba(255,218,173,0.25)", color: "rgba(255,218,173,0.75)", width: "34px", height: "34px", borderRadius: "999px", fontSize: "1.1rem", cursor: "pointer", fontFamily: "inherit" }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="jop-cart-body">
          {!hasItems ? (
            <EmptyCart onClose={onClose} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {items.map((line) => <CartLine key={line.lineId} line={line} />)}
            </div>
          )}
        </div>

        {/* Footer */}
        {hasItems && (
          <div style={{ padding: "1rem 1.25rem 1.25rem", borderTop: "1px solid rgba(255,218,173,0.15)", background: "rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.25rem" }}>
              <span style={{ color: "rgba(255,218,173,0.7)", fontSize: "0.82rem", letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 700 }}>Subtotal</span>
              <span style={{ color: "white", fontSize: "1.25rem", fontWeight: 800 }}>{formatPrice(subtotal)}</span>
            </div>
            <div style={{ color: "rgba(255,218,173,0.55)", fontSize: "0.75rem", marginBottom: "1rem" }}>
              Shipping &amp; tax calculated at checkout.
            </div>
            <button
              onClick={handleCheckout}
              style={{
                width: "100%",
                background: "var(--jop-gold)",
                color: "#1a0a12",
                border: "none",
                borderRadius: "999px",
                padding: "0.9rem",
                fontFamily: "var(--jop-display)",
                fontSize: "1rem",
                fontWeight: 800,
                cursor: "pointer",
                letterSpacing: "0.02em",
                boxShadow: "0 6px 22px rgba(252,179,31,0.35)",
              }}
            >
              Checkout · {formatPrice(subtotal)}
            </button>
          </div>
        )}
      </aside>
    </>
  );
}

// ── Line item ────────────────────────────────────────────────────────────
function CartLine({ line }) {
  const lineTotal = (line.priceCents || 0) * (line.quantity || 0);
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "64px 1fr auto",
      gap: "0.75rem",
      alignItems: "center",
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,218,173,0.15)",
      borderRadius: "12px",
      padding: "0.65rem",
    }}>
      <div style={{ width: "64px", height: "64px", borderRadius: "8px", background: "#000", overflow: "hidden", flexShrink: 0 }}>
        {line.imageUrl ? (
          <img src={line.imageUrl} alt={line.name} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,218,173,0.35)", fontSize: "1.5rem" }}>🥞</div>
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: "white", fontWeight: 700, fontSize: "0.92rem", lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {line.name}
        </div>
        {line.variantName && (
          <div style={{ color: "rgba(255,218,173,0.6)", fontSize: "0.76rem" }}>{line.variantName}</div>
        )}
        <div style={{ marginTop: "0.4rem", display: "flex", alignItems: "center", gap: "0.55rem" }}>
          <QtyStepper
            value={line.quantity}
            onChange={(q) => updateQuantity(line.lineId, q)}
            compact
          />
          <button
            onClick={() => removeFromCart(line.lineId)}
            aria-label={`Remove ${line.name}`}
            style={{
              background: "transparent",
              border: "none",
              color: "rgba(255,218,173,0.5)",
              fontSize: "0.75rem",
              cursor: "pointer",
              fontFamily: "inherit",
              textDecoration: "underline",
              textUnderlineOffset: "2px",
              padding: 0,
            }}
          >
            Remove
          </button>
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ color: "var(--jop-gold)", fontWeight: 800, fontSize: "0.98rem" }}>{formatPrice(lineTotal)}</div>
        {line.quantity > 1 && (
          <div style={{ color: "rgba(255,218,173,0.55)", fontSize: "0.72rem" }}>{formatPrice(line.priceCents)} ea</div>
        )}
      </div>
    </div>
  );
}

function EmptyCart({ onClose }) {
  return (
    <div style={{ textAlign: "center", padding: "3.5rem 1rem 2rem", color: "rgba(255,218,173,0.7)" }}>
      <div style={{ fontSize: "3rem", marginBottom: "0.5rem" }}>🛒</div>
      <div style={{ fontFamily: "var(--jop-script)", fontSize: "1.6rem", color: "var(--jop-gold)", marginBottom: "0.5rem" }}>Nothing in here yet</div>
      <p style={{ fontSize: "0.9rem", lineHeight: 1.55, maxWidth: "320px", margin: "0 auto 1.25rem" }}>
        Browse the shop — add a few things, they'll show up here.
      </p>
      <button
        onClick={onClose}
        style={{
          background: "var(--jop-gold)",
          color: "#1a0a12",
          border: "none",
          borderRadius: "999px",
          padding: "0.6rem 1.25rem",
          fontFamily: "var(--jop-display)",
          fontSize: "0.9rem",
          fontWeight: 800,
          cursor: "pointer",
        }}
      >
        Keep browsing
      </button>
    </div>
  );
}
